import React, { useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { DeleteUserCard } from "../Cards/DeleteUser";
import { Modal } from "@material-ui/core";
import {
  PencilIcon,
  TrashIcon,
  UserCircleIcon,
  CalendarIcon,
  MailIcon,
} from "@heroicons/react/outline";

export const UsersTable = ({ users }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleCloseModal = () => {
    setShowDeleteModal(false);
    setSelectedUser(null);
  };

  // --- MOBILE USER CARD COMPONENT ---
  const MobileUserCard = ({ user }) => (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4 flex flex-col gap-4">
      <div className="flex justify-between items-start">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-lg">
            {user.userName ? user.userName.charAt(0).toUpperCase() : "U"}
          </div>
          <div>
            <h4 className="font-heading font-bold text-gray-900">
              {user.userName || "Unknown"}
            </h4>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">
              Active
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          <Link
            to={`/admin/user/${user._id}`}
            className="p-2 bg-gray-50 rounded-lg text-gray-500 hover:text-primary transition-colors"
          >
            <PencilIcon className="w-4 h-4" />
          </Link>
          <button
            onClick={() => handleDeleteClick(user)}
            className="p-2 bg-red-50 rounded-lg text-red-500 hover:bg-red-100 transition-colors"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 pt-2 border-t border-gray-50">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <MailIcon className="w-4 h-4" />
          <span className="truncate">{user.email}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <CalendarIcon className="w-4 h-4" />
          <span>
            Joined{" "}
            {user.createdAt
              ? format(new Date(user.createdAt), "MMM dd, yyyy")
              : "N/A"}
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <UserCircleIcon className="w-4 h-4" />
          <span className="font-mono text-xs">ID: {user._id?.slice(-8)}</span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* --- DESKTOP VIEW (Hidden on Mobile) --- */}
      <div className="hidden md:block bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/50 border-b border-gray-100">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">
                  User Profile
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Joined
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-right text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {users.map((user, index) => (
                <tr key={index} className="hover:bg-gray-50 transition-colors">
                  {/* User Profile */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                        {user.userName
                          ? user.userName.charAt(0).toUpperCase()
                          : "U"}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-bold text-gray-900">
                          {user.userName || "Unknown"}
                        </div>
                        <div className="text-xs text-gray-400 font-mono">
                          ID: {user._id?.slice(-6)}
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Email */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{user.email}</div>
                  </td>

                  {/* Date */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">
                      {user.createdAt
                        ? format(new Date(user.createdAt), "MMM dd, yyyy")
                        : "-"}
                    </div>
                    <div className="text-xs text-gray-400">
                      {user.createdAt
                        ? format(new Date(user.createdAt), "h:mm a")
                        : ""}
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-bold rounded-full bg-green-100 text-green-800">
                      Active
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/admin/user/${user._id}`}
                        className="p-2 bg-white border border-gray-200 rounded-lg text-gray-400 hover:text-primary hover:border-primary transition-all"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => handleDeleteClick(user)}
                        className="p-2 bg-white border border-gray-200 rounded-lg text-gray-400 hover:text-red-500 hover:border-red-500 transition-all"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- MOBILE VIEW (Visible on Mobile) --- */}
      <div className="md:hidden">
        {users.map((user, index) => (
          <MobileUserCard key={index} user={user} />
        ))}
      </div>

      {/* --- EMPTY STATE --- */}
      {users.length === 0 && (
        <div className="text-center py-12 bg-white rounded-3xl border border-gray-100">
          <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400">
            <UserCircleIcon className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-bold text-gray-900">No Users Found</h3>
          <p className="text-gray-500">The user database is currently empty.</p>
        </div>
      )}

      {/* --- DELETE MODAL --- */}
      <Modal
        open={showDeleteModal}
        onClose={handleCloseModal}
        className="flex items-center justify-center p-4 backdrop-blur-sm"
      >
        <div className="bg-white rounded-3xl shadow-2xl w-full w-full outline-none overflow-hidden">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4 text-red-500">
              <TrashIcon className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-heading font-bold text-gray-900 mb-2">
              Delete User?
            </h3>
            <p className="text-gray-500 mb-6 text-sm">
              Are you sure you want to remove{" "}
              <span className="font-bold text-gray-800">
                {selectedUser?.userName}
              </span>
              ? This action is permanent.
            </p>

            <div className="w-full">
              <DeleteUserCard
                handleClose={handleCloseModal}
                userId={selectedUser?._id}
              />
            </div>

            <button
              onClick={handleCloseModal}
              className="mt-3 w-full py-3 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
